<template>
    <div>
        <BaseGrid class="base-grid--filter">
            <BaseMultiselect
                v-model="selectedtoegewezenBijdrageCategories"
                :label="t('toegewezen_bijdragen.filter.category.label')"
                :inner-label="t('toegewezen_bijdragen.filter.category.placeholder')"
                :options="toegewezenBijdrageCategoryOptions"
            />

            <BaseMultiselect
                v-model="toegewezenBijdragesoortCategories"
                :label="t('toegewezen_bijdragen.filter.sort.label')"
                :inner-label="t('toegewezen_bijdragen.filter.sort.placeholder')"
                :options="toegewezenBijdragesoortCategoryOptions"
            />

            <SearchForm
                :label="t('search.search')"
                :placeholder="t('search.placeholder')"
                @search="onSearch"
                @clear="onClear"
            />
        </BaseGrid>

        <a
            id="focus"
            ref="focus"
        />
        <BaseSpinner v-if="pending" />
        <ToegewezenBijdragenTable
            v-else
            :toekenningen="entries"
        />

        <div v-if="!pending && (entries?.length ?? 0) < 1">
            {{ t('toegewezen_bijdragen.no_results') }}
        </div>

        <BasePaginator
            :current-page="currentPage"
            :total-pages="totalPages"
            :query-param="queryParam"
            @update:current-page="updatePage($event)"
        />
    </div>
</template>

<script setup>
import { isEmpty } from 'lodash-es';
import { endOfYear, formatISO, parse, startOfYear } from 'date-fns';
import getToegewezenBijdragen from '~/graphql/queries/getToegewezenBijdragen.graphql';

const props = defineProps({
    year: {
        type: [Number, String],
        default: null,
        validate: (v) => /^\d{4}$/.test(v) // Check if the year is made up of 4 digits (year)
    },
});

const { year } = toRefs(props);

const dateRangeFilter = computed(() => {
    if (!year.value) {
        return undefined;
    }

    const start = startOfYear(parse(`${year.value}`, 'yyyy', new Date()));
    const end = endOfYear(parse(`${year.value}`, 'yyyy', new Date()));

    return ['and', `>= ${formatISO(start, { representation: 'date' })}`, `<= ${formatISO(end, { representation: 'date' })}`];
});

// template-refs
const focus = ref(null);

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const { variables: defaultVariables } = useCraftGraphql();

const { currentPage, limit, offset, queryParam } = usePaginator(20);

// toegewezenBijdrageCategory
const { options: toegewezenBijdrageCategoryOptions, selected: selectedtoegewezenBijdrageCategories } = await useCategory(
    'toegewezenBijdrageCategory',
    'categorie',
    currentPage
);

// toegewezenBijdragesoortCategory
const { options: toegewezenBijdragesoortCategoryOptions, selected: toegewezenBijdragesoortCategories } = await useCategory(
    'toegewezenBijdragesoort',
    'bijdrage',
    currentPage
);

// FILTER FOR GQL
const relatedToCategories = computed(() => {
    const vars = [];

    if (selectedtoegewezenBijdrageCategories.value) {
        for (const item of selectedtoegewezenBijdrageCategories.value) {
            vars.push({
                group: 'toegewezenBijdrageCategory',
                slug: item,
                site: defaultVariables.site,
            });
        }
    }

    if (toegewezenBijdragesoortCategories.value) {
        for (const item of toegewezenBijdragesoortCategories.value) {
            vars.push({
                group: 'toegewezenBijdragesoort',
                slug: item,
                site: defaultVariables.site,
            });
        }
    }

    return vars;
});

const toekenningenVariables = computed(() => {
    const vars = {
        ...defaultVariables,
        uri: undefined, // We don't need the URI for this query
        section: 'toegewezenBijdragen',
        limit: limit.value,
        offset: offset.value,
        orderBy: 'plainDate DESC',
    };

    if (!isEmpty(relatedToCategories.value)) {
        vars.relatedToCategories = relatedToCategories.value;
    }

    if (route.query.search) {
        vars.search = getSingle(route.query.search);
    }

    if (dateRangeFilter.value) {
        vars.dateRange = dateRangeFilter.value;
    }

    return vars;
});

const { data: toekenningensData, pending } = await useLazyAsyncQuery({
    query: getToegewezenBijdragen,
    variables: toekenningenVariables
});

const { totalPages, entries } = usePaginated(toekenningensData, limit);

const updatePage = (page) => {
    currentPage.value = page;

    nextTick(() => {
        window.scrollTo(0, 0);
        focus.value.focus();
    });
};

const onSearch = (value)  => {
    currentPage.value = 1;
    router.push({
        query: {
            search: value,
            categorie: route?.query?.categorie,
            bijdrage: route?.query?.bijdrage,
            page: 1
        }
    });
};

const onClear = () =>  {
    currentPage.value = 1;
    router.push({
        query: {
            categorie: route?.query?.categorie,
            bijdrage: route?.query?.bijdrage,
            page: 1,
        }
    });
};
</script>
